import React from 'react';
import { Input, Tag } from 'antd';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { Spinner } from '@crc-evans/ui-components';
import { ProjectTabTableEmpty } from './project/ProjectTabTableEmpty';
import '../pages/technical/HeatProfilePage.css';
import './DPR/DPRSettingsPipelinesTab.css';
import './DPR/DPRSettingsSparesTab.css';
import './SpareOrderHistoryModal.css';

const SpareOrderHistoryGrid = ({
  rows,
  isLoading,
  current,
  pageSize,
  sortKey,
  sortDir,
  onToggleSort,
  renderTextColumnFilterTrigger,
  isTextColumnFilterActive,
  onTextColumnFilterChange,
  equipmentTypeValue,
  setEquipmentTypeValue,
  sapKitNoValue,
  setSapKitNoValue,
  sapNumberValue,
  setSapNumberValue,
  descriptionValue,
  setDescriptionValue,
  localColFilters,
  onLocalColFilterChange,
  formatOrderDate,
  formatOrderedByCell,
}) => {
  const textFilterInput = (value, filterType, setter, searchWidth = 120) => (
    <Input
      placeholder="Search"
      value={value ?? ''}
      onChange={(e) => onTextColumnFilterChange(filterType, e.target.value, setter)}
      onClick={(e) => e.stopPropagation()}
      size="small"
      style={{ width: searchWidth }}
      aria-label={`Filter ${filterType}`}
    />
  );

  const localFilterInput = (field, searchWidth = 120) => (
    <Input
      placeholder="Search"
      value={localColFilters[field] ?? ''}
      onChange={(e) => onLocalColFilterChange(field, e.target.value)}
      onClick={(e) => e.stopPropagation()}
      size="small"
      style={{ width: searchWidth }}
      aria-label={`Filter ${field}`}
    />
  );

  const renderSortableHead = (
    label,
    colKey,
    alignLeft,
    heatFacetKey = null,
    searchNode = null,
    { searchWidth = 120, columnSearchActive = false } = {}
  ) => {
    const active = sortKey === colKey;
    const Icon = active && sortDir === 'desc' ? ChevronDown : ChevronUp;
    const facetFilterActive =
      heatFacetKey &&
      typeof isTextColumnFilterActive === 'function' &&
      isTextColumnFilterActive(heatFacetKey);
    const filterIconActive = Boolean(facetFilterActive || columnSearchActive);
    const filterNode =
      heatFacetKey && typeof renderTextColumnFilterTrigger === 'function'
        ? (() => {
            const node = renderTextColumnFilterTrigger(heatFacetKey);
            if (!node || !filterIconActive || facetFilterActive) return node;
            return (
              <span className="spare-order-history-filter-icon-force-active">{node}</span>
            );
          })()
        : null;
    return (
      <div
        className={`dpr-settings-spares-cell dpr-settings-spares-header-cell spare-order-history-grid-cell${
          alignLeft ? ' dpr-settings-spares-cell--left' : ''
        }${searchWidth === 68 ? ' dpr-settings-spares-header-cell--narrow' : ''}`}
      >
        <div className="column-header-with-filter">
          <div
            className={`column-header-text-wrapper spares-column-header-sortable dpr-pipelines-heat-title-row${
              alignLeft ? ' dpr-pipelines-heat-title-row--left' : ''
            }`}
          >
            <span
              className="dpr-pipelines-heat-label dpr-pipelines-heat-label--sortable"
              role="button"
              tabIndex={0}
              onClick={() => onToggleSort(colKey)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onToggleSort(colKey);
                }
              }}
            >
              {label}
            </span>
            <span
              className="custom-sort-icon"
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onToggleSort(colKey);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onToggleSort(colKey);
                }
              }}
            >
              <Icon
                className={`dpr-settings-spares-sort-icon ${active ? 'dpr-settings-spares-sort-icon-active' : ''}`}
                size={14}
                aria-hidden
              />
            </span>
            {filterNode}
          </div>
          {searchNode ? (
            <div
              className={`header-filter-input user-mgmt-header-search${
                columnSearchActive ? ' header-filter-input--has-filter' : ''
              }`}
            >
              <div className="heat-profile-inline-field-shell heat-profile-inline-field-shell--sm heat-profile-header-inline-shell">
                {searchNode}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const isSearchActive = (value) => String(value ?? '').trim().length > 0;

  const renderTypeCell = (row) =>
    row.urgent ? (
      <Tag className="spare-order-history-modal-type-tag spare-order-history-modal-type-tag--urgent">Urgent</Tag>
    ) : (
      <Tag className="spare-order-history-modal-type-tag spare-order-history-modal-type-tag--normal">
        {row.orderTypeLabel || 'Normal'}
      </Tag>
    );

  return (
    <div className="dpr-settings-spares-table-shell spare-order-history-grid-shell">
      <div className="dpr-settings-spares-hscroll">
        <div className="dpr-settings-spares-hscroll-inner spare-order-history-grid-inner">
          <div className="dpr-settings-spares-thead">
            <div className="dpr-settings-spares-header-row dpr-settings-spares-header-row--heat spare-order-history-grid-header">
              <div className="dpr-settings-spares-cell dpr-settings-spares-cell--num dpr-settings-spares-header-cell spare-order-history-grid-cell">
                <div className="column-header-with-filter column-header-with-filter--no-search">
                  <div className="column-header-text-wrapper dpr-pipelines-heat-title-row">
                    <span className="dpr-pipelines-heat-label">#</span>
                  </div>
                </div>
              </div>
              {renderSortableHead(
                'Ordered on',
                'createdAt',
                true,
                '__createdAtDisplay',
                localFilterInput('createdAt', 150),
                { searchWidth: 150, columnSearchActive: isSearchActive(localColFilters.createdAt) }
              )}
              {renderSortableHead(
                'Equipment type',
                'equipmentType',
                true,
                'equipmentType',
                textFilterInput(equipmentTypeValue, 'equipmentType', setEquipmentTypeValue),
                { columnSearchActive: isSearchActive(equipmentTypeValue) }
              )}
              {renderSortableHead(
                'SAP kit',
                'sapKitNo',
                true,
                'sapKitNo',
                textFilterInput(sapKitNoValue, 'sapKitNo', setSapKitNoValue, 110),
                { columnSearchActive: isSearchActive(sapKitNoValue) }
              )}
              {renderSortableHead(
                'SAP number',
                'sapNumber',
                true,
                'sapNumber',
                textFilterInput(sapNumberValue, 'sapNumber', setSapNumberValue),
                { columnSearchActive: isSearchActive(sapNumberValue) }
              )}
              {renderSortableHead(
                'Description',
                'description',
                true,
                'description',
                textFilterInput(descriptionValue, 'description', setDescriptionValue, 140),
                { columnSearchActive: isSearchActive(descriptionValue) }
              )}
              {renderSortableHead(
                'Qty',
                'quantity',
                false,
                null,
                localFilterInput('quantity', 68),
                { searchWidth: 68, columnSearchActive: isSearchActive(localColFilters.quantity) }
              )}
              {renderSortableHead(
                'Type',
                'orderTypeLabel',
                false,
                '__typeDisplay',
                localFilterInput('__typeDisplay', 100),
                { searchWidth: 100, columnSearchActive: isSearchActive(localColFilters.__typeDisplay) }
              )}
              {renderSortableHead(
                'Ordered by',
                'orderedBy',
                true,
                '__orderedByDisplay',
                localFilterInput('orderedBy', 140),
                { columnSearchActive: isSearchActive(localColFilters.orderedBy) }
              )}
            </div>
          </div>
          <div className="dpr-settings-spares-body-scroll">
            {isLoading ? (
              <div className="dpr-settings-spares-loading">
                <Spinner size="medium" />
              </div>
            ) : rows.length === 0 ? (
              <div className="dpr-settings-spares-empty">
                <ProjectTabTableEmpty />
              </div>
            ) : (
              <div className="dpr-settings-spares-rows">
                {rows.map((row, index) => {
                  const orderedByDisplay = formatOrderedByCell(row);
                  return (
                    <div key={row.id ?? index} className="dpr-settings-spares-row spare-order-history-grid-row">
                      <div className="dpr-settings-spares-cell dpr-settings-spares-cell--num spare-order-history-grid-cell">
                        {(current - 1) * pageSize + index + 1}
                      </div>
                      <div
                        className="dpr-settings-spares-cell dpr-settings-spares-cell--left spare-order-history-grid-cell"
                        title={formatOrderDate(row.createdAt)}
                      >
                        {formatOrderDate(row.createdAt)}
                      </div>
                      <div
                        className="dpr-settings-spares-cell dpr-settings-spares-cell--left spare-order-history-grid-cell"
                        title={row.equipmentType}
                      >
                        {row.equipmentType || '—'}
                      </div>
                      <div
                        className="dpr-settings-spares-cell dpr-settings-spares-cell--left spare-order-history-grid-cell"
                        title={row.sapKitNo}
                      >
                        {row.sapKitNo || '—'}
                      </div>
                      <div
                        className="dpr-settings-spares-cell dpr-settings-spares-cell--left spare-order-history-grid-cell"
                        title={row.sapNumber}
                      >
                        {row.sapNumber || '—'}
                      </div>
                      <div
                        className="dpr-settings-spares-cell dpr-settings-spares-cell--left spare-order-history-grid-cell"
                        title={row.description}
                      >
                        {row.description || '—'}
                      </div>
                      <div className="dpr-settings-spares-cell dpr-settings-spares-cell--num spare-order-history-grid-cell">
                        {row.quantity != null && row.quantity !== '' ? row.quantity : '—'}
                      </div>
                      <div className="dpr-settings-spares-cell spare-order-history-grid-cell spare-order-history-type-cell">
                        {renderTypeCell(row)}
                      </div>
                      <div
                        className="dpr-settings-spares-cell dpr-settings-spares-cell--left spare-order-history-grid-cell"
                        title={orderedByDisplay}
                      >
                        {orderedByDisplay}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpareOrderHistoryGrid;
